// frontend/src/pages/TimeTracking/CRManagement.js
// This manages CR/CM HEADERS (the first table in your screenshot)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, Select, Button, DatePicker, Typography,
  Row, Col, Card, Modal, message, Spin, Divider, Table, Space, Popconfirm
} from 'antd';
import {
  SendOutlined, RollbackOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { createCR, getCRsByTicket, updateCR, deleteCR } from '../../utils/timeTrackingService';
import './TimeTrackingForm.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CRManagement = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { user } = useAuth();
    const notificationApi = useNotificationApi();
    
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [ticketInfo, setTicketInfo] = useState(null);
    const [existingCRs, setExistingCRs] = useState([]);
    const [editingCR, setEditingCR] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    
    const ticketId = searchParams.get('ticketId');
    const dateFormat = 'DD/MM/YYYY';
    
    const showError = useCallback((title, content, key = 'globalError') => {
        notificationApi.destroy(key);
        notificationApi.error({
            key,
            message: title,
            description: content,
            duration: 4.5,
            placement: 'topRight',
        });
    }, [notificationApi]);
    
    const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
        notificationApi.success({
            key,
            message: title,
            description,
            duration: 3,
            placement: 'topRight',
        });
    }, [notificationApi]);
    
    useEffect(() => {
        const fetchData = async () => {
            if (!ticketId) {
                setInitialLoading(false);
                return;
            }
            
            try {
                const ticketResponse = await api.get(`/api/tickets/${ticketId}`);
                if (ticketResponse.data.success) {
                    setTicketInfo(ticketResponse.data.data);
                }
                
                const response = await getCRsByTicket(ticketId);
                if (response.success) {
                    setExistingCRs(response.data);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                showError('Failed to Load', 'Could not load ticket information');
            } finally {
                setInitialLoading(false);
            }
        };
        
        fetchData();
    }, [ticketId, showError]);
    
    const handleSubmit = async (values) => {
        setModalLoading(true);
        
        const payload = {
            ticket_id: parseInt(ticketId),
            cr_number: values.cr_number,
            request_date: values.request_date.format('YYYY-MM-DD'),
            description: values.description,
            target_production_date: values.target_production_date ? values.target_production_date.format('YYYY-MM-DD') : null,
            estimated_mandays: values.estimated_mandays || null,
            impact_progress: values.impact_progress || null,
            user_story_no: values.user_story_no || null
        };
        
        try {
            let response;
            if (editingCR) {
                response = await updateCR(editingCR.id, payload);
                if (response.success) {
                    showSuccess('CR/CM Updated', 'CR/CM has been updated successfully');
                }
            } else {
                response = await createCR(payload);
                if (response.success) {
                    showSuccess('CR/CM Created', 'New CR/CM has been created successfully');
                }
            }
            
            if (response.success) {
                const crResponse = await getCRsByTicket(ticketId);
                if (crResponse.success) {
                    setExistingCRs(crResponse.data);
                }
                setModalVisible(false);
                form.resetFields();
                setEditingCR(null);
            } else {
                showError('Operation Failed', response.message || 'Failed to save CR/CM');
            }
        } catch (error) {
            console.error('Error saving CR/CM:', error);
            showError('Operation Failed', error.response?.data?.message || 'Failed to save CR/CM');
        } finally {
            setModalLoading(false);
        }
    };
    
    const handleEdit = (record) => {
        setEditingCR(record);
        form.setFieldsValue({
            cr_number: record.cr_number,
            request_date: moment(record.request_date),
            description: record.description,
            target_production_date: record.target_production_date ? moment(record.target_production_date) : null,
            estimated_mandays: record.estimated_mandays,
            impact_progress: record.impact_progress,
            user_story_no: record.user_story_no
        });
        setModalVisible(true);
    };
    
    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const response = await deleteCR(id);
            if (response.success) {
                showSuccess('CR/CM Deleted', 'CR/CM has been deleted successfully');
                const crResponse = await getCRsByTicket(ticketId);
                if (crResponse.success) {
                    setExistingCRs(crResponse.data);
                }
            } else {
                showError('Delete Failed', response.message || 'Failed to delete CR/CM');
            }
        } catch (error) {
            console.error('Error deleting CR/CM:', error);
            showError('Delete Failed', error.response?.data?.message || 'Failed to delete CR/CM');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAddNew = () => {
        setEditingCR(null);
        form.resetFields();
        const nextNumber = existingCRs.length > 0 ? existingCRs.length + 1 : 1;
        form.setFieldsValue({
            cr_number: `CR #${nextNumber}`
        });
        setModalVisible(true);
    };
    
    const handleViewActivities = (crId) => {
        navigate(`/time-tracking/activities?crMasterId=${crId}`);
    };
    
    const columns = [
        {
            title: 'CR/CM #',
            dataIndex: 'cr_number',
            key: 'cr_number',
            width: 120,
            render: (text, record) => (
                <Text strong style={{ color: '#1890ff' }}>{text}</Text>
            )
        },
        {
            title: 'Request Date',
            dataIndex: 'request_date',
            key: 'request_date',
            width: 120,
            render: (date) => date ? moment(date).format(dateFormat) : '-'
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            width: 300,
        },
        {
            title: 'Impact/Progress',
            dataIndex: 'impact_progress',
            key: 'impact_progress',
            ellipsis: true,
            width: 200,
            render: (text) => text || '-'
        },
        {
            title: 'Est. Mandays',
            dataIndex: 'estimated_mandays',
            key: 'estimated_mandays',
            width: 100,
            align: 'center',
            render: (value) => value ? `${value} days` : '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 220,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button 
                        type="link" 
                        icon={<ClockCircleOutlined />} 
                        onClick={() => handleViewActivities(record.id)}
                        size="small"
                    >
                        Activities
                    </Button>
                    <Button 
                        type="link" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record)}
                        size="small"
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this CR/CM?"
                        description="All activities and time entries under this CR/CM will also be deleted."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button 
                            type="link" 
                            danger 
                            icon={<DeleteOutlined />}
                            size="small"
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];
    
    if (!ticketId) {
        return (
            <div className="time-tracking-page">
                <Card className="time-tracking-card">
                    <div className="empty-state">
                        <Title level={4}>No Ticket Selected</Title>
                        <Text type="secondary">Please select a ticket to manage CR/CM.</Text>
                        <div style={{ marginTop: 20 }}>
                            <Button onClick={() => navigate('/tickets')}>Go to Tickets</Button>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }
    
    if (initialLoading) {
        return (
            <div className="time-tracking-page">
                <div className="time-tracking-loading">
                    <Spin size="large" tip="Loading..." />
                </div>
            </div>
        );
    }
    
    return (
        <div className="time-tracking-page">
            <div className="time-tracking-header">
                <Title level={2} className="time-tracking-title">CR/CM Management</Title>
                <Text className="time-tracking-description">
                    Create and manage Change Requests (CR) and Corrective Maintenance (CM) for this ticket.
                    Each CR/CM can have multiple activities with time entries.
                </Text>
            </div>
            
            <Card className="time-tracking-card">
                <div className="time-tracking-content">
                    {ticketInfo && (
                        <div className="cr-info-box">
                            <div className="cr-info-title">Ticket Information</div>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Text type="secondary">Ticket Number:</Text>
                                    <div><Text strong>{ticketInfo.ticketUuid}</Text></div>
                                </Col>
                                <Col span={16}>
                                    <Text type="secondary">Title:</Text>
                                    <div><Text strong>{ticketInfo.title}</Text></div>
                                </Col>
                            </Row>
                        </div>
                    )}
                    
                    <div style={{ marginBottom: 24, textAlign: 'right' }}>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />} 
                            onClick={handleAddNew}
                            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                        >
                            Create New CR/CM
                        </Button>
                    </div>
                    
                    <div className="existing-cr-list">
                        <div className="existing-cr-header">
                            <Title level={4} className="existing-cr-title">CR/CM List</Title>
                            <Button onClick={() => navigate(`/tickets/${ticketId}`)}>
                                Back to Ticket
                            </Button>
                        </div>
                        
                        {existingCRs.length > 0 ? (
                            <Table
                                columns={columns}
                                dataSource={existingCRs}
                                rowKey="id"
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 1000 }}
                                loading={loading}
                            />
                        ) : (
                            <div className="empty-state">
                                <Text type="secondary">No CR/CM found for this ticket.</Text>
                                <div style={{ marginTop: 16 }}>
                                    <Button type="primary" onClick={handleAddNew} icon={<PlusOutlined />}>
                                        Create First CR/CM
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="form-actions">
                        <Button icon={<RollbackOutlined />} onClick={() => navigate(`/tickets/${ticketId}`)}>
                            Back to Ticket
                        </Button>
                    </div>
                </div>
            </Card>
            
            {/* Create/Edit CR/CM Modal */}
            <Modal
                title={editingCR ? `Edit ${editingCR.cr_number}` : 'Create New CR/CM'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingCR(null);
                    form.resetFields();
                }}
                footer={null}
                width={700}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="cr_number"
                                label="CR/CM Number *"
                                rules={[{ required: true, message: 'Please enter CR/CM number' }]}
                            >
                                <Input placeholder="e.g., CR #4, CM #2" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="request_date"
                                label="Request Date *"
                                rules={[{ required: true, message: 'Please select request date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} format={dateFormat} />
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <Form.Item
                        name="description"
                        label="CR/CM Description *"
                        rules={[{ required: true, message: 'Please enter description' }]}
                    >
                        <TextArea rows={3} placeholder="Describe the change request or corrective maintenance..." />
                    </Form.Item>
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="target_production_date" label="Target Production Date">
                                <DatePicker style={{ width: '100%' }} format={dateFormat} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="estimated_mandays" label="Estimated Mandays">
                                <Input type="number" step={0.5} placeholder="e.g., 5" />
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    <Form.Item name="impact_progress" label="Impact / Progress">
                        <TextArea rows={2} placeholder="e.g., URS received, In progress, etc." />
                    </Form.Item>
                    
                    <Form.Item name="user_story_no" label="User Story No.">
                        <Input placeholder="Enter user story number if applicable" />
                    </Form.Item>
                    
                    <Divider />
                    
                    <div className="form-actions" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                        <Button onClick={() => { setModalVisible(false); setEditingCR(null); form.resetFields(); }}>
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={modalLoading}
                            icon={<SendOutlined />}
                        >
                            {editingCR ? 'Update CR/CM' : 'Create CR/CM'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default CRManagement;